// Which strip of the document Scanbot found is cut out as the MRZ (#245).
//
// - The MRZ strip is cut from the bottom of the document Scanbot found.
// - Open passport, both pages, upright: 125 x 176 mm, width/height about 0.71.
//   Bottom 13% = 22.9 mm (today's strip).
// - One photo page held sideways: 125 x 88 mm, width/height about 1.42.
//   Bottom 26% = 22.9 mm.
// - Both strips are the same 22.9 mm, close to the ICAO TD3 MRZ zone (23.2 mm).
//   The RN SDK finds the MRZ with a model; its 0.26 is only the on-screen
//   highlight (DocumentFrame.tsx line 73).
// - The cut-off 1.0 sits halfway between the two shapes on a ratio scale
//   (0.71 x 1.42 = 1.0), so a crop must be about 40% off to land on the wrong side.
//
// Pure helpers: no Scanbot, no browser APIs.

export const TWO_PAGE_MRZ_CROP = 0.13;
export const ONE_PAGE_MRZ_CROP = 0.26;
export const ONE_PAGE_MIN_ASPECT = 1.0;

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

function toBytes(buffer) {
  if (Object.prototype.toString.call(buffer) === "[object ArrayBuffer]") return new Uint8Array(buffer);
  if (ArrayBuffer.isView(buffer)) return new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
  return null;
}

function isSofMarker(marker) {
  return (
    (marker >= 0xc0 && marker <= 0xc3) ||
    (marker >= 0xc5 && marker <= 0xc7) ||
    (marker >= 0xc9 && marker <= 0xcb) ||
    (marker >= 0xcd && marker <= 0xcf)
  );
}

function sized(width, height) {
  return width > 0 && height > 0 ? { width, height } : null;
}

function readPngSize(bytes) {
  if (bytes.length < 24) return null;
  for (let i = 0; i < PNG_SIGNATURE.length; i++) {
    if (bytes[i] !== PNG_SIGNATURE[i]) return null;
  }
  const u32 = (at) => ((bytes[at] << 24) | (bytes[at + 1] << 16) | (bytes[at + 2] << 8) | bytes[at + 3]) >>> 0;
  return sized(u32(16), u32(20));
}

function readJpegSize(bytes) {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;
  let i = 2;
  while (i + 1 < bytes.length) {
    if (bytes[i] !== 0xff) return null;
    const marker = bytes[i + 1];
    if (marker === 0xff) {
      i += 1; // fill byte
      continue;
    }
    if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      i += 2; // no length
      continue;
    }
    if (marker === 0xd9 || marker === 0xda) return null; // end or scan before any SOF
    if (i + 3 >= bytes.length) return null;
    if (isSofMarker(marker)) {
      if (i + 8 >= bytes.length) return null;
      const height = (bytes[i + 5] << 8) | bytes[i + 6];
      const width = (bytes[i + 7] << 8) | bytes[i + 8];
      return sized(width, height);
    }
    i += 2 + ((bytes[i + 2] << 8) | bytes[i + 3]);
  }
  return null;
}

// Pixel size from the image header, or null if it cannot be read.
export function readImageSize(buffer) {
  const bytes = toBytes(buffer);
  if (!bytes || bytes.length === 0) return null;
  return readPngSize(bytes) || readJpegSize(bytes);
}

export function chooseMrzCrop(imageBuffer) {
  const size = readImageSize(imageBuffer);
  if (!size) return { pageLayout: "unknown", mrzCropRatio: TWO_PAGE_MRZ_CROP };
  const aspect = size.width / size.height;
  const docAspect = Math.round(aspect * 100) / 100;
  if (aspect >= ONE_PAGE_MIN_ASPECT) {
    return { pageLayout: "one_page", mrzCropRatio: ONE_PAGE_MRZ_CROP, docAspect };
  }
  return { pageLayout: "two_page", mrzCropRatio: TWO_PAGE_MRZ_CROP, docAspect };
}

export function mrzCropPolygon(cropRatio) {
  return [
    { x: 0, y: 1 - cropRatio },
    { x: 1, y: 1 - cropRatio },
    { x: 1, y: 1 },
    { x: 0, y: 1 },
  ];
}
